// The one capability check the whole site uses: what the account's plan
// opens (from the API, `account.capabilities`), which plan a locked feature
// needs and whether the Plus trial can still be taken. Pages never compare
// plan names themselves.
import { useCallback, useMemo } from 'react';
import i18n from '@/i18n';
import type { Feature } from '@/lib/features';
import { useAccount } from './queries';

export type PlanKey = 'free' | 'plus' | 'premium';
export const PLAN_LABEL: Record<PlanKey, string> = { free: 'Free', plus: 'Plus', premium: 'Premium' };

export function useCaps() {
  const { capabilities, plan, trial, coinLimits } = useAccount();
  const can = useCallback((f: Feature) => capabilities[f]?.available ?? false, [capabilities]);
  /** the lowest plan that opens the feature */
  const need = useCallback((f: Feature): PlanKey => capabilities[f]?.plan ?? 'premium', [capabilities]);
  return useMemo(() => ({
    can, need,
    plan: plan.id as PlanKey,
    planName: plan.name,
    /** the 5-day Plus trial is still available for this account */
    trialAvailable: trial.available,
    trialDays: trial.days,
    coinLimits,
  }), [can, need, plan.id, plan.name, trial.available, trial.days, coinLimits]);
}
export type Caps = ReturnType<typeof useCaps>;

/** «Доступно с Plus» / «Доступно в Premium» */
export const lockLine = (p: PlanKey) => i18n.t(p === 'premium' ? 'plans:lock.premium' : 'plans:lock.plus');
